import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Section from './ui/Section';
import Container from './ui/Container';
import SectionHeading from './ui/SectionHeading';
import Reveal from './ui/Reveal';
import { ArrowRight } from './ui/Icons';

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  gap: clamp(2rem, 5vw, 4rem);
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const Sticky = styled.div`
  position: sticky;
  top: calc(var(--nav-h) + 2rem);

  @media (max-width: 960px) {
    position: static;
  }
`;

const List = styled.ul`
  display: grid;
  gap: 1rem;
`;

const Card = styled.div`
  display: flex;
  gap: 1.25rem;
  padding: 1.5rem 1.75rem;
  border-radius: var(--radius-md);
  background: var(--surface);
  border: 1px solid var(--border);

  @media (max-width: 560px) {
    padding: 1.25rem;
    gap: 1rem;
  }
`;

const Index = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(192, 204, 48, 0.1);
  border: 1px solid rgba(192, 204, 48, 0.25);
  color: var(--accent);
  font-family: var(--font-display);
  font-size: 0.85rem;
  font-weight: 600;
`;

const Title = styled.h3`
  font-size: 1.125rem;
  margin-bottom: 0.4rem;
`;

const Text = styled.p`
  color: var(--text-2);
  font-size: 0.95rem;

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    margin-top: 0.5rem;
    color: var(--accent);
    font-weight: 600;
  }
`;

const differentiators: { title: string; body: React.ReactNode }[] = [
  {
    title: 'One team, one stack, one invoice.',
    body: 'Design, code, security, and ops handled by the same engineers. No vendor handoffs, no scope confusion, no “that’s a different department.”',
  },
  {
    title: 'Security baked in from day one.',
    body: 'Auth, role-based access, audit logs, rate limiting, and CAPTCHA ship with the first release, not bolted on after a security review or an incident.',
  },
  {
    title: 'Built to be maintained, by anyone.',
    body: 'Versioned APIs, CI/CD, monitoring, and clean handoff docs so the next engineer (yours or ours) can ship on day one.',
  },
  {
    title: 'Running in production.',
    body: (
      <>
        Like Family runs on what we shipped. RB Car Care’s site is live. Raíces y Patitas launches next.
        <br />
        <Link to="/projects">
          See the work <ArrowRight size={16} />
        </Link>
      </>
    ),
  },
];

const WhyUs: React.FC = () => (
  <Section aria-labelledby="why-title">
    <Container>
      <Layout>
        <Sticky>
          <SectionHeading
            id="why-title"
            eyebrow="Why us"
            title="Why teams pick us"
            description="We don't sell discovery as a deliverable. We sell shipped, secured, monitored software, and we stay around to keep it running."
          />
        </Sticky>
        <List>
          {differentiators.map((item, index) => (
            <Reveal as="li" key={item.title} delay={index * 0.06}>
              <Card>
                <Index aria-hidden="true">{index + 1}</Index>
                <div>
                  <Title>{item.title}</Title>
                  <Text>{item.body}</Text>
                </div>
              </Card>
            </Reveal>
          ))}
        </List>
      </Layout>
    </Container>
  </Section>
);

export default WhyUs;
